import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
/* eslint-disable react-refresh/only-export-components -- provider + hook are colocated */
import ConfirmModal from '../components/ui/ConfirmModal';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'danger',
};

export function ConfirmModalProvider({ children }) {
  const [state, setState] = useState({ open: false, ...DEFAULTS });
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((prev) => ({ ...prev, open: false }));
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: options.title ?? DEFAULTS.title,
        message: options.message ?? DEFAULTS.message,
        confirmLabel: options.confirmLabel ?? DEFAULTS.confirmLabel,
        cancelLabel: options.cancelLabel ?? DEFAULTS.cancelLabel,
        variant: options.variant ?? DEFAULTS.variant,
      });
    });
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmModal
        open={state.open}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirmModal() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirmModal must be used within ConfirmModalProvider');
  }
  return ctx;
}
